import { useEffect, useMemo, useState } from "react";

import { api } from "services";
import { Professional } from "types/professional";
import { Quote } from "types/quotes";

import {
  aggregateRede,
  ProfessionalAggregate,
  RedeTransaction,
} from "./aggregate";
import { TimeFrame, timeFrameToRange } from "./timeFrame";

const FETCH_LIMIT = 500;

export interface UseRedeData {
  professionals: { id: number; name: string }[];
  perProfessional: ProfessionalAggregate[];
  transactions: RedeTransaction[];
  isLoading: boolean;
  error: boolean;
  isTruncated: boolean;
}

export function useRedeData(
  companyId: number,
  timeFrame: TimeFrame,
  doctorId: number | null
): UseRedeData {
  const [professionals, setProfessionals] = useState<
    { id: number; name: string }[]
  >([]);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  // Lista de médicos da rede.
  useEffect(() => {
    if (!companyId || companyId <= 0) return;
    const controller = new AbortController();

    (async () => {
      try {
        const { data } = await api.get(`/Company/${companyId}/professionals`, {
          signal: controller.signal,
          // Busca ampla para garantir que todos os médicos da rede apareçam
          // (inclusive os sem transações no período), conforme o design.
          params: { page: 1, size: 200 },
        });
        const list: Professional[] = data ?? [];
        setProfessionals(list.map((p) => ({ id: p.id, name: p.name })));
      } catch (err: any) {
        if (err?.code !== "ERR_CANCELED") {
          console.error(err);
          setError(true);
        }
      }
    })();

    return () => controller.abort();
  }, [companyId]);

  // Transações do período (e médico, se selecionado).
  useEffect(() => {
    if (!companyId || companyId <= 0) return;
    const controller = new AbortController();
    const { startDate, endDate } = timeFrameToRange(timeFrame);

    (async () => {
      setIsLoading(true);
      setError(false);
      try {
        const response = await api.get("/Quote/search", {
          signal: controller.signal,
          params: {
            page: 1,
            size: FETCH_LIMIT,
            onlyOpened: false,
            beginDateCreate: startDate,
            endDateCreate: endDate,
            companyId,
            doctorId: doctorId ?? undefined,
          },
        });
        const data: Quote[] = response.data ?? [];
        setQuotes(data);
        setIsTruncated(data.length >= FETCH_LIMIT);
      } catch (err: any) {
        if (err?.code !== "ERR_CANCELED") {
          console.error(err);
          setError(true);
        }
      } finally {
        setIsLoading(false);
      }
    })();

    return () => controller.abort();
  }, [companyId, timeFrame, doctorId]);

  const { perProfessional, transactions } = useMemo(
    () => aggregateRede(quotes, professionals),
    [quotes, professionals]
  );

  return {
    professionals,
    perProfessional,
    transactions,
    isLoading,
    error,
    isTruncated,
  };
}
